<template>
  <form ref="form" autocomplete="off" @submit.prevent="onSubmitForm">
    <Loader v-if="isLoading" display="absolute"></Loader>
    <Alert v-if="errorMsgs.length" type="danger" :messages="errorMsgs" />

    <div class="form-wrap">
      <div class="mb-30">
        <label for="client" class="form-label mb-20">
          {{ dictionary.clientLabel }}
        </label>
        <div class="form-check-list">
          <div
            v-for="item in clientOptions"
            :key="item.value"
            class="form-check">
            <input
              :id="'client' + item.value"
              v-model="client"
              class="form-check-input"
              type="radio"
              name="client"
              :value="item.value"
              @change="onChangeClient" />
            <label class="form-check-label" :for="'client' + item.value">
              {{ item.text }}
            </label>
          </div>
        </div>
      </div>

      <div v-if="clientIsYes" class="row">
        <!-- CDP -->
        <div class="col-lg-6 mb-30">
          <label for="cdp" class="form-label">
            {{ dictionary.cdpLabel }}
          </label>
          <input
            id="cdp"
            v-model.trim="cdp"
            class="form-control"
            name="cdp"
            type="text"
            maxlength="30" />
        </div>
      </div>

      <div class="row">
        <!-- Firstname -->
        <div class="col-lg-6 mb-30">
          <label for="contactName" class="form-label">
            {{ dictionary.firstnameLabel }}
          </label>
          <input
            id="contactName"
            v-model.trim="firstname"
            class="form-control"
            :class="{ 'has-error': v$.firstname.$error }"
            name="contactName"
            type="text"
            maxlength="30"
            @blur="v$.firstname.$touch" />

          <div v-if="v$.firstname.$error">
            <div v-if="v$.firstname.required.$invalid" class="error">
              {{ dictionary.firstnameRequired }}
            </div>
            <div
              v-if="
                v$.firstname.minLength.$invalid ||
                v$.firstname.maxLength.$invalid
              "
              class="error">
              {{ dictionary.firstnameLength }}
            </div>
          </div>
        </div>

        <!-- Firstname -->
        <div class="col-lg-6 mb-30">
          <label for="contactSurname" class="form-label">
            {{ dictionary.surnameLabel }}
          </label>
          <input
            id="contactSurname"
            v-model.trim="surname"
            class="form-control"
            :class="{ 'has-error': v$.surname.$error }"
            name="contactSurname"
            type="text"
            maxlength="40"
            @blur="v$.surname.$touch" />

          <div v-if="v$.surname.$error">
            <div v-if="v$.surname.required.$invalid" class="error">
              {{ dictionary.surnameRequired }}
            </div>
            <div
              v-if="
                v$.surname.minLength.$invalid || v$.surname.maxLength.$invalid
              "
              class="error">
              {{ dictionary.surnameLength }}
            </div>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-lg-6 mb-30">
          <label for="address" class="form-label">
            {{ dictionary.addressLabel }}
          </label>
          <input
            id="address"
            v-model.trim="address"
            class="form-control"
            name="address"
            type="text"
            maxlength="30" />
        </div>

        <div class="col-lg-6 mb-30">
          <label for="postCode" class="form-label">
            {{ dictionary.postCodeLabel }}
          </label>
          <input
            id="postCode"
            v-model.trim="postCode"
            class="form-control"
            name="postCode"
            type="text"
            maxlength="5" />
        </div>
      </div>

      <div class="row">
        <div class="col-lg-6 mb-30">
          <label for="city" class="form-label">
            {{ dictionary.cityLabel }}
          </label>
          <input
            id="city"
            v-model.trim="city"
            class="form-control"
            name="city"
            type="text"
            maxlength="30" />
        </div>

        <div class="col-lg-6 mb-30">
          <label for="country" class="form-label">
            {{ dictionary.countryLabel }}
          </label>

          <MultiSelect
            id="country"
            v-model="country"
            autocomplete="nope"
            :searchable="true"
            :clear-on-search="true"
            :options="countries"
            :can-deselect="false"
            :no-results-text="dictionary.noResultsText"
            label="text"
            :placeholder="dictionary.countryPlaceholder" />
        </div>
      </div>

      <div class="row">
        <!-- Email -->
        <div class="col-lg-6 mb-30">
          <label for="contactEmail" class="form-label">
            {{ dictionary.emailLabel }}
          </label>
          <input
            id="contactEmail"
            v-model.trim="email"
            class="form-control"
            :class="{ 'has-error': v$.email.$error }"
            maxlength="100"
            name="contactEmail"
            :placeholder="dictionary.emailPlaceholder"
            type="text"
            @blur="v$.email.$touch" />

          <div v-if="v$.email.$error">
            <div v-if="v$.email.required.$invalid" class="error">
              {{ dictionary.emailRequired }}
            </div>
            <div v-if="v$.email.email.$invalid" class="error">
              {{ dictionary.emailRegex }}
            </div>
          </div>
        </div>

        <div class="col-lg-6 mb-30">
          <label for="phone" class="form-label">
            {{ dictionary.phoneLabel }}
          </label>

          <div class="phone-group">
            <MultiSelect
              id="dialingCode"
              v-model="dialingCode"
              autocomplete="nope"
              :searchable="true"
              :clear-on-search="true"
              :options="dialingCodes"
              :can-deselect="false"
              :can-clear="false"
              :no-results-text="dictionary.noResultsText"
              label="text"
              :class="{
                'has-error': v$.dialingCode.$error,
              }">
              <template #singlelabel="{ value }">
                <div class="multiselect-single-label">
                  <img class="multiselect-icon-currency" :src="value.icon" />
                  {{ value.text }}
                </div>
              </template>

              <template #option="{ option }">
                <img class="multiselect-icon-currency" :src="option.icon" />
                {{ option.text }}
              </template>
            </MultiSelect>

            <input
              id="phone"
              v-model.trim="phone"
              class="form-control"
              :class="{ 'has-error': v$.phone.$error }"
              name="phone"
              type="tel"
              @blur="v$.phone.$touch" />
          </div>

          <div v-if="v$.phone.$error">
            <div v-if="v$.phone.required.$invalid" class="error">
              {{ dictionary.phoneRequired }}
            </div>
            <div
              v-if="v$.phone.minLength.$invalid || v$.phone.maxLength.$invalid"
              class="error">
              {{ dictionary.phoneLength }}
            </div>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-lg-6 mb-30">
          <label for="fax" class="form-label">
            {{ dictionary.faxLabel }}
          </label>
          <input
            id="fax"
            v-model.trim="fax"
            class="form-control"
            name="fax"
            type="text"
            maxlength="40" />
        </div>

        <div class="col-lg-6 mb-30">
          <label for="subject" class="form-label">
            {{ dictionary.subjectLabel }}
          </label>
          <select
            id="subject"
            v-model="subject"
            class="form-select"
            name="subject"
            @change="onChangeSubject">
            <option value="">{{ dictionary.subjectPlaceholder }}</option>
            <option
              v-for="item in subjects"
              :key="item.value"
              :value="item.value">
              {{ item.text }}
            </option>
          </select>
        </div>
      </div>

      <div v-if="subjectHasExtraFields" class="row">
        <div class="col-lg-6 mb-30">
          <label for="rentalDate" class="form-label">
            {{ dictionary.rentalDateLabel }}
          </label>

          <DatePicker
            v-model="rentalDate"
            :locale="locale"
            :max-date="maxDate"
            nav-visibility="">
            <template #default="{ togglePopover }">
              <input
                id="rentalDate"
                v-model.trim="rentalDateFormatted"
                class="form-control form-control-calendar"
                name="rentalDate"
                type="text"
                placeholder="dd/mm/yyyy"
                readonly
                @click="togglePopover" />
            </template>
          </DatePicker>
        </div>
        <div class="col-lg-6 mb-30">
          <label for="location" class="form-label">
            {{ dictionary.locationLabel }}
          </label>
          <input
            id="location"
            v-model.trim="location"
            class="form-control"
            name="location"
            type="text"
            maxlength="30" />
        </div>
      </div>

      <div class="mb-15">
        <label for="comments" class="form-label">
          {{ dictionary.commentsLabel }}
        </label>
        <textarea
          id="comments"
          v-model="comments"
          class="form-control"
          :class="{ 'has-error': v$.comments.$error }"
          :placeholder="dictionary.commentsPlaceholder"
          @blur="v$.comments.$touch"></textarea>

        <div v-if="v$.comments.$error">
          <div v-if="v$.comments.required.$invalid" class="error">
            {{ dictionary.commentsRequired }}
          </div>
          <div v-if="v$.comments.maxLength.$invalid" class="error">
            {{ dictionary.commentsLength }}
          </div>
        </div>
      </div>

      <div class="d-flex justify-content-end">
        <small class="bold">
          <span v-if="commentsLength">{{ commentsLength }} /</span>
          {{ commentsMaxLength }}
          {{ dictionary.commentsCharacters }}
        </small>
      </div>
    </div>

    <div class="mb-30"></div>

    <div class="row">
      <div class="col-lg-10 offset-lg-1 mb-30">
        <div class="form-check">
          <input
            id="contactTerms"
            v-model="terms"
            name="contactTerms"
            type="checkbox"
            class="form-check-input"
            autocomplete="off"
            :class="{ 'has-error': v$.terms.$error }" />
          <label class="form-check-label" for="contactTerms">
            <div v-html="dictionary.termsLabel"></div>
          </label>
        </div>

        <div v-if="v$.terms.$error">
          <div v-if="v$.terms.checked.$invalid" class="error">
            {{ dictionary.termsRequired }}
          </div>
        </div>
      </div>
    </div>

    <div class="d-flex justify-content-center mb-30">
      <div>
        <div id="g-recaptcha-contact"></div>
        <div v-if="v$.reCaptchaResponse.$error">
          <div v-if="v$.reCaptchaResponse.required.$invalid" class="error">
            {{ dictionary.reCaptchaRequired }}
          </div>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-lg-4 offset-lg-4">
        <button
          type="submit"
          class="btn btn-primary btn-full-width"
          :disabled="isLoading">
          {{ dictionary.buttonSubmitText }}
        </button>
      </div>
    </div>
  </form>

  <div
    id="modalContactResponse"
    class="modal s-modal s-modal--success"
    tabindex="-1"
    role="dialog">
    <div class="modal-dialog modal-dialog-centered" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <div class="modal-title">
            <div><i class="icon-check-circle"></i></div>
            <div>{{ response?.title }}</div>
          </div>
          <button
            type="button"
            class="btn-close"
            data-bs-dismiss="modal"
            aria-label="Close">
            <i class="icon-close-light"></i>
          </button>
        </div>
        <div class="modal-body" v-html="response?.text"></div>
      </div>
    </div>
  </div>
</template>

<script>
import mixins from './../mixins/mixins'
import MultiSelect from '@vueform/multiselect'
import * as lib from '../../lib/lib'
import axios from 'axios'
import useVuelidate from '@vuelidate/core'
import { email, maxLength, minLength, required } from '@vuelidate/validators'
import Alert from '../components/common/Alert.vue'
import Loader from '../components/common/Loader.vue'
import { DatePicker } from 'v-calendar'
import moment from 'moment'
import { Modal } from 'bootstrap'

export default {
  components: { DatePicker, MultiSelect, Loader, Alert },
  mixins: [mixins],
  setup() {
    return { v$: useVuelidate() }
  },
  data() {
    return {
      locale: document.documentElement.lang,
      scrollOffsetY: 125,
      isLoading: false,
      errorMsgs: [],
      // form properties
      client: '',
      cdp: '',
      firstname: '',
      surname: '',
      address: '',
      email: '',
      dialingCode: '',
      phone: '',

      postCode: '',
      city: '',
      country: '',
      fax: '',
      subject: '',
      rentalDate: null,
      location: '',
      comments: '',
      terms: false,
      // data sources
      countries: [],
      subjects: [],
      clientOptions: [],
      dialingCodes: [],

      commentsMaxLength: 400,

      reCaptchaResponse: '',
      reCaptchaWidget: null,

      maxDate: moment(new Date()).toDate(),

      modalContactResponse: null,

      response: null,
    }
  },
  validations() {
    return {
      firstname: {
        required,
        minLength: minLength(2),
        maxLength: maxLength(30),
      },
      surname: {
        required,
        minLength: minLength(2),
        maxLength: maxLength(40),
      },
      email: {
        required,
        email,
      },
      dialingCode: {
        required,
      },
      phone: {
        required,
        minLength: minLength(5),
        maxLength: maxLength(40),
      },
      comments: { required, maxLength: maxLength(this.commentsMaxLength) },
      terms: {
        checked: (value) => value === true,
      },
      reCaptchaResponse: {
        required,
      },
    }
  },
  computed: {
    rentalDateFormatted() {
      return this.rentalDate
        ? moment(this.rentalDate).format(this.configuration.dateFormat)
        : null
    },

    subjectHasExtraFields() {
      const entry = this.subjects.find((i) => i.value === this.subject)
      return entry ? entry.extraFields : false
    },
    clientIsYes() {
      return this.client ? this.client.toLocaleLowerCase() === 'yes' : false
    },
    commentsLength() {
      return this.comments.length
    },
  },
  mounted() {
    const { countries, subjects, clientOptions, dialingCodes, response } =
      lib.getData('contactData')
    this.countries = countries
    this.subjects = subjects
    this.clientOptions = clientOptions
    this.dialingCodes = dialingCodes
    this.response = response

    this.setPreselectedOptions()

    document.addEventListener('recaptcha-ready', () => {
      this.iniReCaptcha()
    })

    this.modalContactResponse = new Modal(
      document.getElementById('modalContactResponse')
    )
  },
  methods: {
    resetForm() {
      this.$refs.form.reset()

      this.client =
        this.cdp =
        this.firstname =
        this.surname =
        this.address =
        this.email =
        this.dialingCode =
        this.phone =
        this.postCode =
        this.city =
        this.country =
        this.fax =
        this.subject =
        this.location =
        this.comments =
        this.reCaptchaResponse =
          ''
      this.rentalDate = null
      this.terms = false

      this.v$.$reset()
      this.resetReCaptcha()
    },

    onChangeClient() {
      this.cdp = ''
    },

    onChangeSubject() {
      this.rentalDate = null
      this.location = ''
    },

    setPreselectedOptions() {
      const entry = this.dialingCodes.find((i) => i.selected)
      this.dialingCode = this.landDialingCode = entry ? entry.value : ''
    },

    callbackReCaptcha(response) {
      this.reCaptchaResponse = response
    },

    expiredCallbackReCaptha() {
      this.reCaptchaResponse = ''
    },

    iniReCaptcha() {
      document.getElementById('g-recaptcha-contact').innerHTML = ''

      const grecaptcha = window.grecaptcha

      this.reCaptchaWidget = grecaptcha.render('g-recaptcha-contact', {
        sitekey: this.configuration.reCaptchaPublicKey,
        callback: this.callbackReCaptcha,
        'expired-callback': this.expiredCallbackReCaptha,
      })
    },

    resetReCaptcha() {
      this.reCaptchaResponse = ''
      window.grecaptcha.reset(this.reCaptchaWidget)
    },

    onSubmitForm() {
      this.v$.$validate()

      if (this.v$.$invalid) {
        this.$nextTick(() => {
          lib.scrollToValidation(this.scrollOffsetY)
        })
        return false
      }

      this.isLoading = true

      const formData = new FormData()

      formData.append('CDP', this.cdp)
      formData.append('Name', this.firstname)
      formData.append('Surname', this.surname)
      formData.append('Address', this.address)
      formData.append('Postcode', this.postCode)
      formData.append('City', this.city)
      formData.append('Country', this.country)
      formData.append('Email', this.email)
      formData.append('PhoneDialingCode', this.dialingCode)
      formData.append('Phone', this.phone)
      formData.append('Fax', this.fax)
      formData.append('Subject', this.subject)
      formData.append('RentalDate', this.rentalDateFormatted)
      formData.append('Location', this.location)
      formData.append('Comments', this.comments)
      formData.append('AcceptTerms', this.terms)
      formData.append('g-recaptcha-response', this.reCaptchaResponse)

      axios
        .post(
          this.configuration.endpointSubmitData,
          formData,
          this.getAxiosConfig()
        )
        .then((response) => {
          const { hasError, errorMessages } = response.data
          // response has error
          if (hasError) {
            this.handleError(errorMessages)
            return false
          }

          this.modalContactResponse.show()
          this.resetForm()
          this.handleSuccess()
        })
        .catch((errors) => {
          this.handleError([this.dictionary.defaultErrorMessage])
          console.error(errors)
        })
    },

    handleSuccess() {
      this.isLoading = false
      this.errorMsgs = []
    },

    handleError(messages) {
      this.errorMsgs = messages
      this.isLoading = false
    },
  },
}
</script>

<style scoped lang="scss">
input:read-only {
  cursor: pointer;
  background-color: white;
  color: black;
}
</style>
